import React from 'react'
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  DiffOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { Button, Dropdown, MenuProps, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { CustomReport } from '../../../../redux/context/customReports/types'
import { useCtxDeleteCustomReport } from '../../../../features/customReport/queries/report/useCtxDeleteCustomReport'
import { getTitleByLanguage } from './customReportTable/utils'
import { useDeleteConfirmModal } from '../../../../shared/modals'
import { useCalculatedReport, useCalculatedReportContext } from './CalculatedReportContext'

interface CustomReportActionsProps {
  editModal: (sec: any | null) => void
  openEdit: (report: CustomReport) => void
  setreportModalVisible: (visible: boolean) => void
}

const CustomReportActions: React.FC<CustomReportActionsProps> = ({ openEdit, editModal, setreportModalVisible }) => {
  const { data: calculatedReport } = useCalculatedReport()
  const { t } = useTranslation()
  const { mutateAsync: deleteReport } = useCtxDeleteCustomReport()
  const deleteConfirmModal = useDeleteConfirmModal()
  const { setReportId } = useCalculatedReportContext()

  const handleDelete = async (id?: CustomReport['id']) => {
    if (!id) return
    await deleteReport(id)
    if (calculatedReport?.id === id) {
      setReportId(undefined)
    }
  }

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      case 'edit':
        calculatedReport?.id && openEdit(calculatedReport)
        break
      case 'delete':
        deleteConfirmModal({
          content: calculatedReport && getTitleByLanguage(calculatedReport),
          onOk: () => handleDelete(calculatedReport?.id)
        })
        break
      default:
        break
    }
  }

  const menuProps: MenuProps = {
    items: [
      { icon: <EditOutlined />, label: t('global:edit'), key: 'edit', 'data-cy': 'report-edit' },
      {
        icon: <DeleteOutlined />,
        danger: true,
        label: `${t('global:delete')} ${t('global:report').toLowerCase()}`,
        key: 'delete',
        'data-cy': 'report-delete'
      }
    ],
    style: { zIndex: 9000 },
    onClick: handleMenuClick
  }

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <span aria-hidden="true" data-cy="add-report" onClick={() => setreportModalVisible(true)}>
          <DiffOutlined /> {t('global:report')}
        </span>
      )
    },
    {
      key: '2',
      disabled: !calculatedReport,
      label: (
        <span aria-hidden="true" data-cy="add-section" onClick={() => calculatedReport && editModal(null)}>
          <AppstoreAddOutlined /> {t('global:section')}
        </span>
      )
    }
  ]

  return (
    <Space>
      <Dropdown placement="bottom" menu={{ items }}>
        <Button data-cy="report-add-button" type="primary" style={{ minWidth: 80 }}>
          <PlusOutlined />
          {t('global:new')}
        </Button>
      </Dropdown>
      <Dropdown data-cy="report-actions-dropdown" placement="bottomRight" menu={menuProps}>
        <Button icon={<EllipsisOutlined />} />
      </Dropdown>
    </Space>
  )
}

export default CustomReportActions
